import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import ApiClient from '../api/apiClient'
import db from '../utilities/firebase-firestore'
import {
  BookedSeat,
  BusinessUnit,
  ReportDateRange,
  ReportingContextType,
  SeatBooking,
  Team
} from '../types/andTypes'
import { TeamExtensions } from '../constants'
import { LoginContext } from './login-context'
import { ApiContext } from './api-context'

export const ReportingContext = createContext({} as ReportingContextType)
const apiClient = ApiClient.getInstance(db)

interface ReportingProviderProps {
  children: ReactNode
}

interface andiBUTeamMap {
  [key: string]: {
    businessUnitId: string
    teamId?: string
  }
}

export const ReportingProvider = ({ children }: ReportingProviderProps) => {
  const { authenticated, user } = useContext(LoginContext)
  const [rawSeatBookingData, setRawSeatBookingData] = useState<SeatBooking[]>([])
  const [reportSeatBookingsByTeam, setReportSeatBookingsByTeam] = useState<SeatBooking[]>([])
  const [reportSeatBookingsByBU, setReportSeatBookingsByBU] = useState<SeatBooking[]>([])
  const [reportBusinessUnit, setReportBusinessUnit] = useState<BusinessUnit | undefined>()
  const [reportTeam, setReportTeam] = useState<Team | undefined>()
  const [reportDateRange, setReportDateRange] = useState<ReportDateRange | undefined>()
  const { getANDis } = useContext(ApiContext)
  const [firstBookedSeat, setFirstBookedSeat] = useState<SeatBooking | undefined>()

  const augmentBookingsWithTeam = (bookings: SeatBooking[]) => {
    return bookings.map((booking: SeatBooking) => {
      const andiToBUTeamMap = getANDis().reduce((acc, curr) => {
        if (curr.userId) {
          acc[curr.userId] = {
            businessUnitId: curr.businessUnit,
            teamId: curr?.team
          }
        }
        return acc
      }, {} as andiBUTeamMap)

      const getTeamId = (seat: BookedSeat) => {
        const teamId = andiToBUTeamMap[seat.userId]?.teamId
        if (teamId === undefined) {
          return TeamExtensions.UNDEFINED_TEAM.id
        }
        const businessUnitId = andiToBUTeamMap[seat.userId].businessUnitId
        if (businessUnitId !== seat.businessUnit) {
          return TeamExtensions.OTHER_BUSINESS_UNIT.id
        }
        return teamId
      }

      return {
        ...booking,
        seats: booking.seats.map(seat => {
          const team = getTeamId(seat)
          return { ...seat, teamId: team }
        })
      }
    })
  }

  const filterSeatBookingsByBU = (
    bookings: SeatBooking[],
    bu: BusinessUnit | undefined
  ): SeatBooking[] => {
    if (bu === null || bu === undefined) {
      return bookings
    }

    const bookingsByBU = bookings.map(booking => ({
      ...booking,
      seats: booking.seats.filter(seat => seat.businessUnit === bu.id)
    }))

    return bookingsByBU
  }

  const filterSeatBookingsByTeam = (
    bookings: SeatBooking[],
    team: Team | undefined
  ): SeatBooking[] => {
    if (team == null || team.id === TeamExtensions.ALL_TEAMS.id) {
      return bookings
    }

    const bookingsByTeam = bookings.map(booking => ({
      ...booking,
      seats: booking.seats.filter(seat => seat.teamId === team.id)
    }))

    return bookingsByTeam
  }

  const removeEmptySeats = (bookings: SeatBooking[]) =>
    bookings.filter(booking => booking.seats.length > 0)

  const firstDateOfBooking = (bookings: SeatBooking[]) =>
    bookings?.reduce(function (a, b) {
      return a.dateBooked < b.dateBooked ? a : b
    })

  const filterSeats = (bookings: SeatBooking[]) => {
    if (authenticated && user?.email) {
      const augmentedBookings = augmentBookingsWithTeam(bookings)
      const seatsByBU = removeEmptySeats(
        filterSeatBookingsByBU(augmentedBookings, reportBusinessUnit)
      )
      setReportSeatBookingsByBU(seatsByBU)
      const seatsByTeam = removeEmptySeats(filterSeatBookingsByTeam(seatsByBU, reportTeam))
      setReportSeatBookingsByTeam(seatsByTeam)
    }
  }

  useEffect(() => {
    filterSeats(rawSeatBookingData)
  }, [getANDis, reportBusinessUnit, reportTeam])

  useEffect(() => {
    if (authenticated && user?.email && reportDateRange) {
      const unsubscribe = apiClient.subscribeToReportingBookings(reportDateRange, bookings => {
        setRawSeatBookingData(bookings)
        filterSeats(bookings)
        setFirstBookedSeat(firstDateOfBooking(bookings))
      })

      return () => {
        unsubscribe()
      }
    }
  }, [reportDateRange])

  return (
    <ReportingContext.Provider
      value={{
        reportSeatBookingsByBU,
        reportSeatBookingsByTeam,
        reportBusinessUnit,
        setReportBusinessUnit,
        reportTeam,
        setReportTeam,
        reportDateRange,
        setReportDateRange,
        firstBookedSeat
      }}
    >
      {children}
    </ReportingContext.Provider>
  )
}
